import {useEffect, useMemo, useState} from 'react';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';

import styles from './CustomRangeSelector.module.scss';

const cx = classnames.bind(styles);
const THUMB_SIZE = 24;

const CustomRangeSelector = ({
  className,
  name,
  id = 'test',
  min = 0,
  minRange = 10,
  max,
  minLabel,
  maxLabel,
  step,
  value = [],
  onChange,
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [minValue, setMinValue] = useState(value?.min || min);
  const [maxValue, setMaxValue] = useState(value?.max || max);

  const onInputChange = function () {
    const newValue = [
      minValue === min ? undefined : minValue,
      maxValue === max ? undefined : maxValue
    ]
    onChange && onChange(newValue, {id, name})
  }

  useEffect(() => {
    setMinValue(value?.min || min);
    setMaxValue(value?.max || max);
  }, [value]);

  useEffect(() => {
    if (isMounted) {
      onInputChange()
    }

    if (!isMounted) {
      setIsMounted(true)
    }
  }, [minValue, maxValue]);

  const onMinChange = e => {
    e.preventDefault();
    const value = parseFloat(e.target.value);
    if (maxValue - minRange < value) {
      return
    }
    // the new min value is the value from the event.
    // it should not exceed the current max value!
    const newMinVal = Math.min(value, maxValue - step);
    setMinValue(newMinVal);
  };
  const onMaxChange = e => {
    e.preventDefault();
    const value = parseFloat(e.target.value);
    if (minValue + minRange > value) {
      return
    }
    // the new max value is the value from the event.
    // it must not be less than the current min value!
    const newMaxVal = Math.max(value, minValue + step);
    setMaxValue(newMaxVal);
  };

  const minLabelText = useMemo(function () {
    return typeof minLabel === 'function' ? minLabel(minValue) : minLabel
  }, [minValue, minLabel])

  const maxLabelText = useMemo(function () {
    return typeof maxLabel === 'function' ? maxLabel(maxValue) : maxLabel
  }, [maxValue, maxLabel])

  const innerRailPosition = useMemo(function () {
    const possibleRange = max - min;
    const leftPosition = possibleRange/100 * (minValue - min);
    const rightPosition = possibleRange/100 * (max - maxValue);
    const leftTranslate = THUMB_SIZE * leftPosition/100;
    const rightTranslate = THUMB_SIZE * rightPosition/100;

    return {
      left: `calc(${leftPosition}% - ${leftTranslate}px)`,
      right: `calc(${rightPosition}% - ${rightTranslate}px)`,
    }
  }, [minValue, maxValue, max, min])

  return (
    <div className={cx('root', className)}>
      <input
        onChange={onMinChange}
        value={minValue}
        className={cx('input', 'min')}
        type="range"
        id={id ? id + '-min' : null}
        name={name + '-min'}
        min={min}
        max={max}
        step={step}
      />
      <label
        className={cx('label', 'min')}
        htmlFor={id ? id + '-min' : null}
        style={{left: innerRailPosition.left}}
      >
        {minLabelText}
      </label>

      <div className={cx('rail')}>
        <span style={innerRailPosition} className={cx('inner-rail')}></span>
      </div>

      <input
        onChange={onMaxChange}
        value={maxValue}
        className={cx('input', 'max')}
        type="range"
        id={id ? id + '-max' : null}
        name={name + '-max'}
        min={min}
        max={max}
        step={step}
      />
      <label
        className={cx('label', 'max')}
        style={{right: innerRailPosition.right}}
        htmlFor={id ? id + '-max' : null}
      >
        {maxLabelText}
      </label>
    </div>
  )
}

CustomRangeSelector.propTypes = {
  className: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number
}

export default CustomRangeSelector;
